import type { ErrorEvent } from "@sentry/react";

// `httpClientIntegration` synthesises one error per failed response, with a
// message that is only the status code and a stack that always resolves to the
// XHR patch in react-network-interceptor. Title and frame are therefore
// identical for every failed request in the app, and Sentry merges them all:
// PLATFORM-LCM-5M9 held `/synchronize/v2`, `/budgets`, `/dco-instream`,
// `/insertion-order/{uuid}/lock` and more under one "status code: 400" issue,
// which no one can triage. Group on the request that actually failed instead.
//
// Returns null for anything that is not an httpClient auto-capture, leaving
// Sentry's default grouping alone.
export function getHttpClientFingerprint(
  event: ErrorEvent,
): Array<string> | null {
  if (!isHttpClientEvent(event)) {
    return null;
  }
  const status = getResponseStatusCode(event);
  const method = event.request?.method;
  const url = event.request?.url;
  if (status === null || !method || !url) {
    return null;
  }
  const endpoint = getEndpointShape(url);
  if (!endpoint) {
    return null;
  }
  return ["http-client", method.toUpperCase(), endpoint, String(status)];
}

// A path segment that is the literal `undefined`/`null`/`NaN` means the request
// was built from a parameter that had not resolved yet — a frontend defect, not
// backend validation. This is what keeps 400s worth reporting once the rest of
// them are dropped as expected form feedback.
const UNRESOLVED_SEGMENTS = new Set(["undefined", "null", "NaN"]);

export function hasUnresolvedPathSegment(url: string): boolean {
  // The php backend is reached through a relative url that `new URL` rejects;
  // stripping query and hash by hand keeps the check working there too.
  const pathname = parseUrl(url)?.pathname ?? url.split(/[?#]/)[0] ?? "";
  return pathname
    .split("/")
    .some((segment) => UNRESOLVED_SEGMENTS.has(segment));
}

const HTTP_CLIENT_MECHANISM_PREFIX = "auto.http.client";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const DIGITS_PATTERN = /^\d+$/;

export function isHttpClientEvent(event: ErrorEvent): boolean {
  return Boolean(
    event.exception?.values?.some((value) =>
      value.mechanism?.type?.startsWith(HTTP_CLIENT_MECHANISM_PREFIX),
    ),
  );
}

function getResponseStatusCode(event: ErrorEvent): number | null {
  const response = event.contexts?.response;
  if (
    typeof response === "object" &&
    response !== null &&
    "status_code" in response &&
    typeof response.status_code === "number"
  ) {
    return response.status_code;
  }
  return null;
}

// Identifiers in the path would split one endpoint into one issue per campaign,
// and identifiers in query *values* would do the same, so only the parameter
// names are kept — they still separate endpoints that differ solely by filter.
function getEndpointShape(url: string): string | null {
  const parsed = parseUrl(url);
  if (!parsed) {
    return null;
  }
  const path = parsed.pathname.split("/").map(normalizeSegment).join("/");
  const queryKeys = [...new Set(parsed.searchParams.keys())].sort();
  const query = queryKeys.length > 0 ? `?${queryKeys.join("&")}` : "";
  return `${parsed.host}${path}${query}`;
}

// httpClientIntegration always records an absolute URL, so no base is passed:
// a base would turn a malformed URL into a path relative to the page instead of
// letting it fall back to Sentry's default grouping.
function parseUrl(url: string): URL | null {
  try {
    return new URL(url);
  } catch {
    return null;
  }
}

export function normalizeSegment(segment: string): string {
  if (UUID_PATTERN.test(segment)) {
    return ":id";
  }
  if (DIGITS_PATTERN.test(segment)) {
    return ":n";
  }
  return segment;
}
